import React, { useState, useEffect } from 'react';
import {
  Phone, Mail, MapPin, Clock, Send, CheckCircle, AlertCircle,
  MessageSquare, Calendar, Users, Shield, Zap, CreditCard,
  ArrowRight, Star, Globe, Building2, Headphones, ChevronRight, TrendingUp, Play
} from 'lucide-react';
import Header from "./header";
import Footer from "./footer";
const config = window.APP_CONFIG;
const apiUrl = config.API_URL;
export default function Contact() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    preferredContact: 'email'
  });
  // const apiUrl = process.env.REACT_APP_API_URL || 'http://13.202.42.97:81/bmrapi/api';

  const [formStatus, setFormStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // const validatePhone = (phone) => {
  //   // Allow various phone number formats
  //   const phoneRegex = /^[\+]?[1-9][\d]{0,11}$/;
  //   const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
  //   return phoneRegex.test(cleanPhone);

  // };

  // Phone validation function
  const validatePhone = (phone) => {
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, ''); // Clean any spaces or symbols

    // If the phone number starts with a '+', it should be between 2 to 15 digits in total.
    if (phone.startsWith('+')) {
      if (cleanPhone.length < 2 || cleanPhone.length > 13) {
        return 'Enter valid phone number';
      }
    }
    // If no country code is provided, it should be exactly 10 digits.
    else if (cleanPhone.length !== 10) {
      return 'Enter valid phone number';
    }

    // If the input is valid
    return '';
  };


  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'fullName':
        if (!value.trim()) {
          error = 'Full name is required';
        } else if (value.trim().length < 2) {
          error = 'Full name must be at least 2 characters';
        } else if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
          error = 'Full name can only contain letters and spaces';
        }
        break;

      case 'email':
        if (!value.trim()) {
          error = 'Email address is required';
        } else if (!validateEmail(value.trim())) {
          error = 'Please enter a valid email address';
        }
        break;

      case 'phone':
        if (value.trim() && !validatePhone(value.trim())) {
          error = 'Please enter a valid phone number';
        }
        break;

      case 'company':
        if (value.trim() && value.trim().length < 2) {
          error = 'Company name must be at least 2 characters';
        }
        break;

      case 'subject':
        if (!value.trim()) {
          error = 'Subject is required';
        } else if (value.trim().length < 5) {
          error = 'Subject must be at least 5 characters';
        } else if (value.trim().length > 100) {
          error = 'Subject must be less than 100 characters';
        }
        break;

      case 'message':
        if (!value.trim()) {
          error = 'Message is required';
        } else if (value.trim().length < 10) {
          error = 'Message must be at least 10 characters';
        } else if (value.trim().length > 1000) {
          error = 'Message must be less than 1000 characters';
        }
        break;

      default:
        break;
    }

    return error;
  };

  // Validate entire form
  const validateForm = () => {
    const newErrors = {};

    // Validate all fields
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
      }
    });

    // Additional validation: if phone is preferred contact method, phone should be provided
    if (formData.preferredContact === 'phone' && !formData.phone.trim()) {
      newErrors.phone = 'Phone number is required when selected as preferred contact method';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    console.log('Subscribing email:', email);
    setEmail('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Real-time validation for immediate feedback
    const error = validateField(name, value);
    if (error) {
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleInputChangePhone = (e) => {
    const { name, value } = e.target;

    // Restrict input to numbers and a leading '+' for country code
    const cleanedValue = value.replace(/[^0-9+]/g, ''); // Only numbers and '+' allowed

    // Update form data
    setFormData((prev) => ({
      ...prev,
      [name]: cleanedValue,
    }));

    // Clear error for this field when the user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    // Real-time validation for immediate feedback
    const error = validatePhone(cleanedValue);
    if (error) {
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setFormStatus('');

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare the request body according to your API schema
      const requestBody = {
        Type: activeTab.charAt(0).toUpperCase() + activeTab.slice(1), // Capitalize first letter
        FullName: formData.fullName,
        EmailAddress: formData.email,
        PhoneNo: formData.phone,
        CompanyName: formData.company,
        Subject: formData.subject,
        Message: formData.message,
        ContactMethod: formData.preferredContact.charAt(0).toUpperCase() + formData.preferredContact.slice(1)
      };

      console.log('Sending request:', requestBody);

      const response = await fetch(`${apiUrl}/enquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Success:', result);
        setFormStatus('success');

        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: '',
          preferredContact: 'email'
        });

        // Clear any remaining errors
        setErrors({});

        // Clear success message after 5 seconds
        setTimeout(() => setFormStatus(''), 5000);
      } else {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        setFormStatus('error');
        setTimeout(() => setFormStatus(''), 5000);
      }
    } catch (error) {
      console.error('Network error:', error);
      setFormStatus('error');
      setTimeout(() => setFormStatus(''), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    // {
    //   icon: <Phone className="w-6 h-6" />,
    //   title: "Phone Support",
    //   details: "+91 8425930405",
    //   description: "Mon-Fri 9:30 AM-6:30 PM IST",
    //   action: "Call Now"
    // },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email Support",
      details: "admin@anvitechsolutions.com",
      description: "Response within 4 hours",
      action: "Send Email"
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Book a session",
      details: "Schedule consultation",
      description: "30-minute session",
      action: "Book Now"
    }
  ];

  const supportCategories = [
    {
      id: 'general',
      title: 'General Inquiry',
      icon: <MessageSquare className="w-5 h-5" />,
      description: 'Questions about our services'
    },
    {
      id: 'technical',
      title: 'Technical Support',
      icon: <Shield className="w-5 h-5" />,
      description: 'Integration and API help'
    },
    {
      id: 'sales',
      title: 'Sales & Pricing',
      icon: <CreditCard className="w-5 h-5" />,
      description: 'Pricing and custom solutions'
    },
    {
      id: 'partnership',
      title: 'Partnership',
      icon: <Users className="w-5 h-5" />,
      description: 'Business partnerships'
    }
  ];

  const officeLocations = [
    {
      city: "Mumbai",
      address: "C-403, Hetal Arch premises, opp. Natraj Market, S.V. Road, Malad West, Mumbai, Maharashtra 400064",
      phone: "+91 8425930405",
      email: "admin@anvitechsolutions.com"
    }
  ];

  const getInputClassName = (fieldName) => {
    const baseClass = "w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all";
    const errorClass = errors[fieldName] ? "border-red-500 focus:ring-red-500" : "border-gray-300";
    return `${baseClass} ${errorClass}`;
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">

      <Header />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Get in <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Touch</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Ready to transform your payment processing? Our experts are here to help you find the perfect solution for your business.
              </p>
            </div>
            <div className="relative">
              <div className="w-full h-80 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-lg flex items-center justify-center">
                <div className="text-center p-5">
                  {/* <Building2 className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                  <p className="text-gray-600">Contact Image Placeholder</p> */}
                  <img src="./ATBG.jpg" alt="Contact Us" className="w-full h-full object-cover rounded-lg shadow-lg" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Methods - Dark Theme */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">How Can We Help You?</h2>
            <p className="text-gray-300">Choose your preferred way to connect with our team</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {contactMethods.map((method, index) => {
              const getHref = () => {
                switch (method.action) {
                  case 'Call Now':
                    return 'tel:+918425930405';
                  case 'Send Email':
                    return 'mailto:admin@anvitechsolutions.com';
                  case 'Book Now':
                    return '#contact-form';
                  default:
                    return '#';
                }
              };

              const handleClick = (e) => {
                if (method.action === 'Book Now') {
                  e.preventDefault();
                  document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' });
                }
              };

              return (
                <div key={index} className="bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer border border-gray-700 hover:border-blue-500">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform">
                    {method.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{method.title}</h3>
                  <p className="text-blue-400 font-medium mb-1">{method.details}</p>
                  <p className="text-gray-400 text-sm mb-4">{method.description}</p>
                  <a
                    href={getHref()}
                    onClick={handleClick}
                    className="text-blue-400 font-medium text-sm flex items-center space-x-1 group-hover:text-blue-300 hover:underline"
                  >
                    <span>{method.action}</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Contact Form - Light Theme */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info & Offices */}
            <div className="space-y-8">
              {/* Why Choose Us */}
              

              {/* Office Locations */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
                  <Building2 className="w-6 h-6 text-blue-600" />
                  <span>Our Offices</span>
                </h3>
                <div className="space-y-6">
                  {officeLocations.map((office, index) => (
                    <div key={index} className="border-l-4 border-blue-500 pl-4">
                      <h4 className="font-semibold text-gray-900 text-lg">{office.city}</h4>
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex items-center space-x-2">
                          <MapPin className="w-4 h-4" />
                          <span>{office.address}</span>
                        </div>
                        {/* <div className="flex items-center space-x-2">
                          <Phone className="w-4 h-4" />
                          <span>{office.phone}</span>
                        </div> */}
                        <div className="flex items-center space-x-2">
                          <Mail className="w-4 h-4" />
                          <span>{office.email}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
                  <Clock className="w-6 h-6 text-blue-600" />
                  <span>Business Hours</span>
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monday - Friday</span>
                    <span className="font-medium text-gray-900">10:30 AM - 7:30 PM IST</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Saturday & Sunday</span>
                    <span className="font-medium text-gray-900">Closed</span>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-blue-600">Emergency Support:</span> Available 24/7 for critical issues
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Send us a Message</h2>

              {/* Category Tabs */}
              <div className="flex flex-wrap gap-2 mb-8" id="contact-form">
                {supportCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setActiveTab(category.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${activeTab === category.id
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                  >
                    <span>{category.icon}</span>
                    <span>{category.title}</span>
                  </button>
                ))}
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className={getInputClassName('fullName')}
                      placeholder="Rahul Sharma"
                      required
                    />
                    {errors.fullName && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.fullName}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={getInputClassName('email')}
                      placeholder="rahul@company.com"
                      required
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.email}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChangePhone}
                      className={`border p-2 rounded ${getInputClassName('phone')}`}
                      placeholder="+91 22 2883 6400"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <svg className="w-4 h-4 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="8" cy="8" r="8" />
                          <path d="M5 5l6 6M5 11L11 5" stroke="#fff" />
                        </svg>
                        {errors.phone}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className={getInputClassName('company')}
                      placeholder="Your Company"
                    />
                    {errors.company && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.company}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className={getInputClassName('subject')}
                    placeholder="How can we help you?"
                    required
                  />
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.subject}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message * <span className="text-gray-500 text-xs">({formData.message.length}/1000)</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="6"
                    className={`${getInputClassName('message')} resize-none`}
                    placeholder="Tell us more about your requirements..."
                    required
                  ></textarea>
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Contact Method
                  </label>
                  <div className="flex space-x-6">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="preferredContact"
                        value="email"
                        checked={formData.preferredContact === 'email'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Email</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="preferredContact"
                        value="phone"
                        checked={formData.preferredContact === 'phone'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Phone</span>
                    </label>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className={`w-full py-4 rounded-lg font-medium text-lg transition-all flex items-center justify-center space-x-2 ${isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl'
                    }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Send Message</span>
                    </>
                  )}
                </button>

                {formStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-green-700">Message sent successfully! We'll get back to you soon.</span>
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <span className="text-red-700">Failed to send message. Please try again or contact us directly.</span>
                  </div>
                )}
              </div>
            </div>




          </div>
        </div>
      </section>

      <section className="py-8 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-white/5 rounded-lg animate-bounce transform rotate-45" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-20 left-20 w-16 h-16 bg-white/10 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
          <div className="absolute bottom-40 right-10 w-20 h-20 bg-white/5 rounded-lg animate-bounce transform -rotate-12" style={{ animationDelay: '0.5s' }}></div>

          <div className="absolute top-1/4 left-1/4 animate-float">
            <Zap className="w-8 h-8 text-white/20" />
          </div>
          <div className="absolute top-1/3 right-1/3 animate-float" style={{ animationDelay: '1.5s' }}>
            <Shield className="w-6 h-6 text-white/20" />
          </div>
          <div className="absolute bottom-1/3 left-1/3 animate-float" style={{ animationDelay: '3s' }}>
            <TrendingUp className="w-7 h-7 text-white/20" />
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          {/* Main Content */}


          <div className="space-y-4 animate-fadeInUp">
            <h2 className="text-2xl sm:text-3xl lg:text-3xl font-bold text-white leading-tight animate-slideInLeft">
              Empowering Business Through Technology.....
            </h2>
          </div>


        </div>

        <style jsx>{`
                  @keyframes float {
                    0%, 100% { transform: translateY(0px) rotate(0deg); }
                    33% { transform: translateY(-10px) rotate(5deg); }
                    66% { transform: translateY(5px) rotate(-5deg); }
                  }
                  
                  @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(30px); }
                    to { opacity: 1; transform: translateY(0); }
                  }
                  
                  @keyframes slideInLeft {
                    from { opacity: 0; transform: translateX(-50px); }
                    to { opacity: 1; transform: translateX(0); }
                  }
                  
                  @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(50px); }
                    to { opacity: 1; transform: translateX(0); }
                  }
                  
                  @keyframes slideInUp {
                    from { opacity: 0; transform: translateY(40px); }
                    to { opacity: 1; transform: translateY(0); }
                  }
                  
                  .animate-float {
                    animation: float 4s ease-in-out infinite;
                  }
                  
                  .animate-fadeInUp {
                    animation: fadeInUp 0.8s ease-out forwards;
                    opacity: 0;
                  }
                  
                  .animate-slideInLeft {
                    animation: slideInLeft 0.8s ease-out forwards;
                    opacity: 0;
                  }
                  
                  .animate-slideInRight {
                    animation: slideInRight 0.8s ease-out forwards;
                    opacity: 0;
                  }
                  
                  .animate-slideInUp {
                    animation: slideInUp 0.8s ease-out forwards;
                    opacity: 0;
                  }
                `}</style>
      </section>

      <Footer />

    </div>
  );
}
